<template>
  <div>
    <!-- Filtros -->
    <v-row class="mb-4 align-center">
      <v-col cols="12" sm="6" md="3">
        <v-text-field
          v-model="filters.q"
          label="Buscar por RFC, razón social..."
          dense
          outlined
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          @keyup.enter="fetchInvoices(1)"
          @click:clear="onClear('q')"
        />
      </v-col>

      <v-col cols="6" sm="3" md="2">
        <v-menu
          v-model="menuStart"
          :close-on-content-click="false"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              :value="filters.startDate"
              label="Desde"
              dense
              outlined
              hide-details
              readonly
              clearable
              prepend-inner-icon="mdi-calendar"
              @click:clear="onClear('startDate')"
            />
          </template>
          <v-date-picker
            v-model="filters.startDate"
            no-title
            locale="es"
            @input="menuStart = false; fetchInvoices(1)"
          />
        </v-menu>
      </v-col>

      <v-col cols="6" sm="3" md="2">
        <v-menu
          v-model="menuEnd"
          :close-on-content-click="false"
          offset-y
          min-width="auto"
        >
          <template #activator="{ on, attrs }">
            <v-text-field
              v-bind="attrs"
              v-on="on"
              :value="filters.endDate"
              label="Hasta"
              dense
              outlined
              hide-details
              readonly
              clearable
              prepend-inner-icon="mdi-calendar"
              @click:clear="onClear('endDate')"
            />
          </template>
          <v-date-picker
            v-model="filters.endDate"
            no-title
            locale="es"
            @input="menuEnd = false; fetchInvoices(1)"
          />
        </v-menu>
      </v-col>

      <v-col cols="6" sm="3" md="2">
        <v-select
          v-model="filters.type"
          :items="cfdiTypes"
          label="Tipo CFDI"
          dense
          outlined
          hide-details
          clearable
          @change="fetchInvoices(1)"
        />
      </v-col>

      <v-col cols="6" sm="3" md="auto" class="d-flex align-center">
        <v-btn
          :color="color.primaryVariant"
          dark
          small
          class="rounded-pill"
          @click="fetchInvoices(1)"
        >
          <v-icon small class="mr-1">mdi-magnify</v-icon>
          Buscar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabla -->
    <v-data-table
      :headers="headers"
      :items="invoices"
      :loading="loading"
      :server-items-length="total"
      :options.sync="tableOptions"
      :footer-props="{
        itemsPerPageText: 'Filas por página',
        itemsPerPageOptions: [10, 20, 50],
      }"
      class="elevation-0"
      disable-sort
    >
      <template #body="{ items }">
        <tbody>
          <tr
            v-for="(item, index) in items"
            :key="item.id || index"
            :class="[
              $vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-table-row' : '',
              index % 2 === 1 ? 'amber lighten-5' : '',
            ]"
          >
            <!-- Folio -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[0].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                <span class="font-weight-medium">
                  {{ item.series ? item.series + "-" : "" }}{{ item.folio_number }}
                </span>
              </div>
            </td>

            <!-- Fecha -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[1].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                {{ formatDate(item.date) }}
              </div>
            </td>

            <!-- Cliente / RFC -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[2].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                <div class="caption font-weight-bold">{{ item.customer && item.customer.legal_name }}</div>
                <div class="caption grey--text">{{ item.customer && item.customer.tax_id }}</div>
              </div>
            </td>

            <!-- Total -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[3].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                ${{ item.total | currency }}
              </div>
            </td>

            <!-- Tipo CFDI -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[4].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                <span class="caption font-weight-bold">{{ item.type | cfdiLabel }}</span>
              </div>
            </td>

            <!-- Estatus -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[5].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                <v-chip small :color="statusColor(item.status)" dark>
                  {{ item.status | statusLabel }}
                </v-chip>
              </div>
            </td>

            <!-- Concepto -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[6].text }}
              </div>
              <div :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : ''">
                <v-chip
                  v-if="item.concept"
                  x-small
                  :color="conceptColor(item.concept)"
                  dark
                >
                  {{ conceptLabel(item.concept) }}
                </v-chip>
                <span v-else class="grey--text">—</span>
              </div>
            </td>

            <!-- Acciones -->
            <td :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row' : ''">
              <div class="font-weight-bold mr-2 v-data-table__mobile-row__header d-flex d-sm-none">
                {{ headers[7].text }}
              </div>
              <div
                :class="$vuetify.breakpoint.xsOnly ? 'v-data-table__mobile-row__cell' : 'd-flex'"
              >
                <v-btn
                  icon
                  small
                  :loading="downloadingId === item.id + 'pdf'"
                  :disabled="!!downloadingId"
                  title="Descargar PDF"
                  @click="downloadInvoice(item, 'pdf')"
                >
                  <v-icon small color="red darken-1">mdi-file-pdf-box</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  :loading="downloadingId === item.id + 'zip'"
                  :disabled="!!downloadingId"
                  title="Descargar ZIP"
                  @click="downloadInvoice(item, 'zip')"
                >
                  <v-icon small color="blue darken-1">mdi-folder-zip-outline</v-icon>
                </v-btn>
              </div>
            </td>
          </tr>
        </tbody>
      </template>

      <template #no-data>
        <div class="text-center py-8 grey--text">
          <v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
          <p class="mt-2">No se encontraron facturas</p>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { colors } from "@/constants/colors";
import { downloadFile } from "@/utils";

export default {
  name: "InvoiceRepository",

  filters: {
    currency(val) {
      if (val == null) return "0.00";
      return Number(val)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    cfdiLabel(type) {
      return (
        { I: "Ingreso", E: "Egreso", T: "Traslado", N: "Nómina", P: "Pago" }[
          type
        ] || type
      );
    },
    statusLabel(status) {
      return (
        { valid: "Vigente", cancelled: "Cancelada", draft: "Borrador" }[
          status
        ] || status
      );
    },
  },

  data() {
    return {
      color: colors,
      loading: false,
      invoices: [],
      total: 0,
      downloadingId: null,
      menuStart: false,
      menuEnd: false,
      filters: {
        q: "",
        startDate: "",
        endDate: "",
        type: "",
      },
      tableOptions: {
        page: 1,
        itemsPerPage: 20,
      },
      cfdiTypes: [
        { text: "Ingreso (I)", value: "I" },
        { text: "Egreso (E)", value: "E" },
        { text: "Pago (P)", value: "P" },
        { text: "Traslado (T)", value: "T" },
        { text: "Nómina (N)", value: "N" },
      ],
      headers: [
        { text: "Folio", value: "folio_number" },
        { text: "Fecha", value: "date" },
        { text: "Cliente / RFC", value: "customer" },
        { text: "Total", value: "total" },
        { text: "Tipo CFDI", value: "type" },
        { text: "Estatus", value: "status" },
        { text: "Concepto", value: "concept" },
        { text: "Descargar", value: "actions", sortable: false },
      ],
    };
  },

  watch: {
    "tableOptions.page"(newPage, oldPage) {
      if (newPage !== oldPage) this.fetchInvoices(newPage);
    },
    "tableOptions.itemsPerPage"() {
      this.fetchInvoices(1);
    },
  },

  async created() {
    await this.fetchInvoices(1);
  },

  methods: {
    ...mapActions("invoice", ["fetchOrganizationInvoiceRepository"]),

    async fetchInvoices(page) {
      this.loading = true;
      try {
        const params = {
          page: page || this.tableOptions.page,
          limit: this.tableOptions.itemsPerPage,
        };
        if (this.filters.q) params.q = this.filters.q;
        if (this.filters.startDate) params.startDate = this.filters.startDate;
        if (this.filters.endDate) params.endDate = this.filters.endDate;
        if (this.filters.type) params.type = this.filters.type;

        const res = await this.fetchOrganizationInvoiceRepository(params);
        if (res.status) {
          this.invoices = res.data.invoices || [];
          this.total = res.data.total || 0;
          if (res.data.page) this.tableOptions.page = res.data.page;
        }
      } finally {
        this.loading = false;
      }
    },

    async downloadInvoice(item, format) {
      this.downloadingId = item.id + format;
      try {
        const response = await this.axios.get(
          `/invoices/organization/${item.id}/download`,
          { params: { format }, responseType: "arraybuffer" }
        );
        const ext = format === "pdf" ? "pdf" : "zip";
        const mimeType =
          format === "pdf" ? "application/pdf" : "application/zip";
        const folio = item.series
          ? `${item.series}-${item.folio_number}`
          : item.folio_number;
        downloadFile(response.data, mimeType, `factura-${folio}.${ext}`);
      } catch {
        // silently fail; production can add snackbar notification here
      } finally {
        this.downloadingId = null;
      }
    },

    onClear(field) {
      this.filters[field] = "";
      this.fetchInvoices(1);
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      return new Date(dateStr).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    statusColor(status) {
      return (
        {
          valid: "green darken-1",
          cancelled: "red darken-1",
          draft: "grey darken-1",
        }[status] || "grey"
      );
    },

    conceptColor(concept) {
      return (
        {
          EVENTO: "blue darken-1",
          MEMBRESIA: "purple darken-1",
          PRODUCTO_SERVICIO: "teal darken-1",
        }[concept] || "grey darken-1"
      );
    },

    conceptLabel(concept) {
      return (
        {
          EVENTO: "Evento",
          MEMBRESIA: "Membresía",
          PRODUCTO_SERVICIO: "Prod/Serv",
        }[concept] || concept
      );
    },
  },
};
</script>

<style scoped>
table,
tr,
td {
  border: none !important;
}
</style>
